/**
 * Main App component with routing and theme
 */
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { TooltipProvider } from '@/components/ui/tooltip';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/Layout';
import { Login } from '@/pages/Login';
import { Callback } from '@/pages/Callback';
import { Dashboard } from '@/pages/Dashboard';
import { VouchersList } from '@/pages/VouchersList';
import { VoucherDetail } from '@/pages/VoucherDetail';
import { Upload } from '@/pages/Upload';
import { GmailConnections } from '@/pages/GmailConnections';
import { useAuthStore } from '@/store/authStore';

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const { initialize, isAuthenticated } = useAuthStore();

  useEffect(() => {
    initialize().catch(console.error);
  }, [initialize]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  return (
    <ErrorBoundary>
      <TooltipProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
              }
            />
            <Route
              path="/callback"
              element={<Callback />}
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout darkMode={darkMode} onToggleDarkMode={() => setDarkMode(!darkMode)}>
                    <Navigate to="/dashboard" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout darkMode={darkMode} onToggleDarkMode={() => setDarkMode(!darkMode)}>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vouchers"
              element={
                <ProtectedRoute>
                  <Layout darkMode={darkMode} onToggleDarkMode={() => setDarkMode(!darkMode)}>
                    <VouchersList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vouchers/:id"
              element={
                <ProtectedRoute>
                  <Layout darkMode={darkMode} onToggleDarkMode={() => setDarkMode(!darkMode)}>
                    <VoucherDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <Layout darkMode={darkMode} onToggleDarkMode={() => setDarkMode(!darkMode)}>
                    <Upload />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gmail"
              element={
                <ProtectedRoute>
                  <Layout darkMode={darkMode} onToggleDarkMode={() => setDarkMode(!darkMode)}>
                    <GmailConnections />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={darkMode ? 'dark' : 'light'}
          />
        </BrowserRouter>
      </TooltipProvider>
    </ErrorBoundary>
  );
}

export default App;
